// src/views/admin/items/component/AddEditDrawer.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';

import { Input } from '@/components/ui/inputs/input';
import { InputTextarea } from '@/components/ui/inputs/input-textarea';
import { InputSelect } from '@/components/ui/inputs/input-select';
import InputRadio from '@/components/ui/inputs/input-radio';

import { addItemService, updateItemService, getByIdItemService } from '@/services/ItemServices';
import { getItemCategoryService } from '@/services/ItemCategoryServices';
import { getTaxService } from '@/services/TaxServices';
import { fileUpload } from '@/services/CommonService';
import validationErrors from '@/lib/validationErrors';
import SidebarDrawer from '@/components/common/SidebarDrawer';
import ToastMe from '@/components/ui/ToastMe';

const DEFAULTS = {
  name: '',
  price: '',
  item_category_id: null,
  tax_id: null,
  file_name: '',
  // radios as strings so inputs are controlled from the start
  item_type: '1',
  is_featured: '1',
  status: '1',
  caution: '',
  description: '',
};

const AddEditDrawer = ({ titleText, activeDrawer, setActiveDrawer, getTableData, editDataId = '' }) => {
  const dispatch = useDispatch();

  // Company / branch (needed by backend)
  const companyIdFromStore = useSelector((s) => s.Auth?.data?.data?.company_id);
  const branchIdFromStore  = useSelector((s) => s.Branch?.active?._id);
  const company_id = companyIdFromStore || (typeof localStorage !== 'undefined' ? localStorage.getItem('company_id') : '');
  const branch_id  = branchIdFromStore  || (typeof localStorage !== 'undefined' ? localStorage.getItem('branch_id')  : '');

  const selectTaxRef  = useRef(null);
  const selectItemRef = useRef(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULTS,
  });

  const [taxesOptionList, setTaxesOptionList] = useState([]);
  const [itemCategoriesOptionList, setItemCategories] = useState([]);
  // local state mirrors radios (keeps UI snappy)
  const [itemType, setItemType] = useState(1);
  const [itemIsFeatured, setIsFeatured] = useState(1);
  const [itemStatus, setItemStatus] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormSumbmit, setIsFormSumbmit] = useState(false);

  useEffect(() => {
    if (!activeDrawer) return;

    // fresh form each open
    reset(DEFAULTS);
    setItemType(1);
    setIsFeatured(1);
    setItemStatus(1);

    // load selects
    getItemCategory();
    getTaxes();

    // editing?
    if (editDataId) {
      getDataById(editDataId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrawer, editDataId]);

  const handleDrawerClose = () => {
    reset(DEFAULTS);
    setActiveDrawer(false);
    document.body.style.overflowY = 'auto';
    getTableData();
  };

  // Handle radio changes - InputRadio passes value directly, not an event
  const handleChangeItemType = (value) => {
    const numValue = Number(value ?? '1');
    setValue('item_type', String(value));
    setItemType(numValue);
  };

  const handleChangeIsFeatured = (value) => {
    const numValue = Number(value ?? '1');
    setValue('is_featured', String(value));
    setIsFeatured(numValue);
  };

  const handleChangeStatus = (value) => {
    const numValue = Number(value ?? '1');
    setValue('status', String(value));
    setItemStatus(numValue);
  };

  const getTaxes = () => {
    setIsLoading(true);
    dispatch(getTaxService({ search: '', page: 1, limit: 100, extra_filter: {} }))
      .then((res) => {
        const rows = res?.data?.data?.data || [];
        setTaxesOptionList(rows.map((t) => ({ value: t?._id, label: t?.name })));
        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  const getItemCategory = () => {
    setIsLoading(true);
    dispatch(getItemCategoryService({ search: '', page: 1, limit: 100, extra_filter: {} }))
      .then((res) => {
        const rows = res?.data?.data?.data || [];
        setItemCategories(rows.map((c) => ({ value: c?._id, label: c?.name })));
        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  const getDataById = (id) => {
    setIsLoading(true);
    dispatch(getByIdItemService(id))
      .then((res) => {
        const d = res?.data?.data || {};

        // Map API → form values
        const mapped = {
          name: d.name ?? '',
          price: d.price ?? '',
          item_category_id: d.item_category?._id ?? d.item_category_id ?? null,
          tax_id: d.item_tax?._id ?? d.tax_id ?? null,
          file_name: d.file_name ?? '',
          item_type: String(d.item_type ?? '1'),
          is_featured: String(d.is_featured ?? '1'),
          status: String(d.status ?? '1'),
          caution: d.caution ?? '',
          description: d.description ?? '',
        };

        reset(mapped);
        setItemType(Number(mapped.item_type));
        setIsFeatured(Number(mapped.is_featured));
        setItemStatus(Number(mapped.status));
        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  const handleFileUpload = (event) => {
    const file_preview = event.target.files?.[0];
    if (!file_preview || !file_preview.type?.includes('image/')) {
      ToastMe('Please upload only image files', 'error');
      return;
    }
    dispatch(fileUpload({ file: file_preview }, 'product'))
      .then((res) => {
        const out = res?.data;
        if (out?.fileName) setValue('file_name', out.fileName, { shouldDirty: true });
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
      });
  };

  const onSubmit = (data) => {
    setIsFormSumbmit(true);

    // Ensure company present
    const cid = company_id && String(company_id).trim();
    if (!cid) {
      ToastMe('No company selected. Please select a company before saving.', 'error');
      setIsFormSumbmit(false);
      return;
    }

    const payload = {
      ...data,
      company_id: cid,
      ...(branch_id && String(branch_id).toLowerCase() !== 'all' ? { branch_id } : {}),
      // normalize numbers
      price: Number(data.price ?? 0),
      item_type: Number(data.item_type ?? 1),
      is_featured: Number(data.is_featured ?? 1),
      status: Number(data.status ?? 1),
    };

    const svc = editDataId ? updateItemService(editDataId, payload) : addItemService(payload);

    dispatch(svc)
      .then(() => {
        reset(DEFAULTS);
        setItemType(1);
        setIsFeatured(1);
        setItemStatus(1);
        setIsFormSumbmit(false);
        handleDrawerClose();
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsFormSumbmit(false);
      });
  };

  return (
    <SidebarDrawer
      icon="lab lab-add-circle-line"
      titleText={titleText}
      activeDrawer={activeDrawer}
      handleDrawerClose={handleDrawerClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Name */}
          <Controller
            name="name"
            control={control}
            rules={{ required: true, minLength: 2, maxLength: 50 }}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}                // keep controlled
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Name"
                type="text"
                errorData={{ minLength: '2', maxLength: '50' }}
                errorType={errors?.name?.type}
              />
            )}
          />

          {/* Price */}
          <Controller
            name="price"
            control={control}
            rules={{ required: true, min: 0 }}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}                // keep controlled
                labelStyleClassName="db-field-title required"
                size="sm"
                labelText="Price"
                type="number"
                step="0.01"
                errorData={{ min: '0' }}
                errorType={errors?.price?.type}
              />
            )}
          />

          {/* Category (controlled select) */}
          <Controller
            name="item_category_id"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <InputSelect
                ref={selectItemRef}
                id="item_category_id"
                labelStyleClassName="db-field-title required"
                labelText="Category"
                value={itemCategoriesOptionList.find((o) => o.value === field.value) ?? null}
                onChange={(opt) => field.onChange(opt?.value ?? null)}
                options={itemCategoriesOptionList}
                errorType={errors?.item_category_id?.type}
              />
            )}
          />

          {/* Tax (controlled select) */}
          <Controller
            name="tax_id"
            control={control}
            render={({ field }) => (
              <InputSelect
                ref={selectTaxRef}
                id="tax_id"
                labelStyleClassName="db-field-title"
                labelText="Tax (Including)"
                value={taxesOptionList.find((o) => o.value === field.value) ?? null}
                onChange={(opt) => field.onChange(opt?.value ?? null)}
                options={taxesOptionList}
                errorType={errors?.tax_id?.type}
              />
            )}
          />

          {/* Image (UNcontrolled file input; don't pass value) */}
          <div className="form-input-item">
            <Input
              onChange={handleFileUpload}
              size="sm"
              labelText="Image"
              type="file"
              id="tmp_file_name"
            />
          </div>

          {/* Item Type */}
          <div className="form-input-item">
            <label className="db-field-title required">Item Type</label>
            <div className="db-field-radio-group">
              <InputRadio
                id="Veg"
                name="item_type"
                labelText="Veg"
                value="1"
                checked={itemType === 1}
                onChange={handleChangeItemType}
              />
              <InputRadio
                id="NonVeg"
                name="item_type"
                labelText="Non Veg"
                value="2"
                checked={itemType === 2}
                onChange={handleChangeItemType}
              />
            </div>
          </div>

          {/* Is Featured */}
          <div className="form-input-item">
            <label className="db-field-title required">Is Featured</label>
            <div className="db-field-radio-group">
              <InputRadio
                id="FeaturedYes"
                name="is_featured"
                labelText="Yes"
                value="2"
                checked={itemIsFeatured === 2}
                onChange={handleChangeIsFeatured}
              />
              <InputRadio
                id="FeaturedNo"
                name="is_featured"
                labelText="No"
                value="1"
                checked={itemIsFeatured === 1}
                onChange={handleChangeIsFeatured}
              />
            </div>
          </div>

          {/* Status */}
          <div className="form-input-item">
            <label className="db-field-title required">Status</label>
            <div className="db-field-radio-group">
              <InputRadio
                id="Active"
                name="status"
                labelText="Active"
                value="1"
                checked={itemStatus === 1}
                onChange={handleChangeStatus}
              />
              <InputRadio
                id="Inactive"
                name="status"
                labelText="Inactive"
                value="2"
                checked={itemStatus === 2}
                onChange={handleChangeStatus}
              />
            </div>
          </div>

          {/* Caution */}
          <Controller
            name="caution"
            control={control}
            rules={{ minLength: 5, maxLength: 200 }}
            render={({ field }) => (
              <InputTextarea
                {...field}
                value={field.value ?? ''}                // keep controlled
                labelText="Caution"
                errorData={{ minLength: '5', maxLength: '200' }}
                errorType={errors?.caution?.type}
              />
            )}
          />

          {/* Description */}
          <Controller
            name="description"
            control={control}
            rules={{ minLength: 5, maxLength: 200 }}
            render={({ field }) => (
              <InputTextarea
                {...field}
                value={field.value ?? ''}                // keep controlled
                labelText="Description"
                errorData={{ minLength: '5', maxLength: '200' }}
                errorType={errors?.description?.type}
              />
            )}
          />
        </div>

        <div className="form-col-12">
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="submit"
              className="db-btn py-2 text-white bg-primary"
              disabled={isFormSumbmit || isLoading}
            >
              <i className="lab lab-save" /> <span>Save</span>
              {isFormSumbmit && (
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
            </button>
            <button
              type="button"
              className="modal-btn-outline modal-close"
              onClick={handleDrawerClose}
            >
              <i className="lab lab-close" /> <span>Close</span>
            </button>
          </div>
        </div>
      </form>
    </SidebarDrawer>
  );
};

export default AddEditDrawer;
